import { OrgStructure } from '@commenda/api';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useStore,
  type CoordinateExtent,
  type EdgeTypes,
  type NodeTypes,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ChartMinimap,
  ChartViewportSync,
  ReadableViewportHelper,
  useReadableFitView,
} from '../ChartViewport';
import { DiagramToolbar } from './components/DiagramToolbar';
import { EmptyState } from './components/EmptyState';
import { EntityNode } from './components/EntityNode';
import { OwnershipEdge } from './components/OwnershipEdge';
import { UnlinkedEntitiesPanel } from './components/UnlinkedEntitiesPanel';
import { copyOrgChartPngToClipboard, exportOrgChartPdf, exportOrgChartPng } from './exportOrgChart';
import { buildGraph } from './graph/buildGraph';
import { ENTITY_NODE_HEIGHT, ENTITY_NODE_WIDTH, layoutGraph } from './graph/layout';
import type { EntityNode as EntityNodeType } from './graph/types';

interface OrgDiagramProps {
  structure: OrgStructure;
  companyId: number;
  companyName?: string;
  onEntityClick?: (entityId: number) => void;
  onUnlinkedClick?: (entityId: number) => void;
  onExportPng?: () => void;
  onExportPdf?: () => void;
  onCopyPng?: () => void;
  // Floating canvas chrome. Default: nothing rendered.
  topLeftSlot?: ReactNode;
  topRightSlot?: ReactNode;
  // Suppress the in-canvas UnlinkedEntitiesPanel — for callers that surface
  // unlinked entities elsewhere (e.g. a page-level side rail).
  hideUnlinkedPanel?: boolean;
  // Fires once after nodes have landed in the store and the DOM viewport
  // has settled — read this signal before screenshotting the chart.
  onReady?: (rf: ReactFlowInstance) => void;
  hideToolbar?: boolean;
}

function CaptureGate({ onReady }: { onReady: (rf: ReactFlowInstance) => void }) {
  const rf = useReactFlow();
  const nodeCount = useStore((s) => s.nodeLookup.size);
  const calledRef = useRef(false);
  useEffect(() => {
    if (calledRef.current || nodeCount === 0) return;
    // Two RAFs: first commits node DOM, second lets the initial fit
    // tick through. The capture doesn't depend on the viewport transform
    // (we re-translate via PADDING) but waiting avoids racing a half-laid-out
    // tree on dense graphs.
    const f1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (calledRef.current) return;
        calledRef.current = true;
        onReady(rf);
      });
    });
    return () => cancelAnimationFrame(f1);
  }, [onReady, nodeCount, rf]);
  return null;
}

const NODE_TYPES: NodeTypes = { entity: EntityNode };
const EDGE_TYPES: EdgeTypes = { ownership: OwnershipEdge };

// Slack on each side, expressed as a fraction of the canvas viewport. Lets
// the user drag the chart far enough off-edge to peek behind floating overlays
// (e.g. UnlinkedEntitiesPanel on the right) without losing all anchor.
const PAN_OVERSCROLL_FRACTION = 0.5;

function computeTranslateExtent(
  nodes: EntityNodeType[],
  canvasSize: { width: number; height: number } | null,
): CoordinateExtent {
  if (nodes.length === 0) {
    return [
      [-Infinity, -Infinity],
      [Infinity, Infinity],
    ];
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + ENTITY_NODE_WIDTH);
    maxY = Math.max(maxY, n.position.y + ENTITY_NODE_HEIGHT);
  }
  // Pre-measure (and tiny canvases) fall back to a sane minimum so the
  // chart isn't pinned hard to its bbox before ResizeObserver fires.
  const padX = Math.max(100, (canvasSize?.width ?? 0) * PAN_OVERSCROLL_FRACTION);
  const padY = Math.max(100, (canvasSize?.height ?? 0) * PAN_OVERSCROLL_FRACTION);
  return [
    [minX - padX, minY - padY],
    [maxX + padX, maxY + padY],
  ];
}

const ENTITY_NODE_SIZE = { width: ENTITY_NODE_WIDTH, height: ENTITY_NODE_HEIGHT };

function ToolbarWithExport({
  nodes,
  companyId,
  companyName,
  onExportPngOverride,
  onExportPdfOverride,
  onCopyPngOverride,
}: {
  nodes: EntityNodeType[];
  companyId: number;
  companyName?: string;
  onExportPngOverride?: () => void;
  onExportPdfOverride?: () => void;
  onCopyPngOverride?: () => void;
}) {
  const rf = useReactFlow();
  const handlePng = useCallback(() => {
    if (onExportPngOverride) return onExportPngOverride();
    void exportOrgChartPng(rf, companyName);
  }, [onExportPngOverride, rf, companyName]);
  const handlePdf = useCallback(() => {
    if (onExportPdfOverride) return onExportPdfOverride();
    void exportOrgChartPdf(rf, companyId, companyName);
  }, [onExportPdfOverride, rf, companyId, companyName]);
  const handleCopy = useCallback(() => {
    if (onCopyPngOverride) return onCopyPngOverride();
    copyOrgChartPngToClipboard(rf).catch((err) => {
      console.error('Failed to copy chart to clipboard', err);
    });
  }, [onCopyPngOverride, rf]);
  const fitReadableView = useReadableFitView({
    nodes,
    nodeSize: ENTITY_NODE_SIZE,
    minZoom: ReadableViewportHelper.ORG_MIN_ZOOM,
  });
  const handleFitView = useCallback(() => fitReadableView({ duration: 250 }), [fitReadableView]);
  return (
    <DiagramToolbar
      onExportPng={handlePng}
      onExportPdf={handlePdf}
      onCopyPng={handleCopy}
      onFitView={handleFitView}
    />
  );
}

// Layout is memoized on the structure reference so pan/zoom never re-runs dagre.
const OrgDiagram = ({
  structure,
  companyId,
  companyName,
  onEntityClick,
  onUnlinkedClick,
  onExportPng,
  onExportPdf,
  onCopyPng,
  topLeftSlot,
  topRightSlot,
  hideUnlinkedPanel,
  onReady,
  hideToolbar,
}: OrgDiagramProps) => {
  const { positionedNodes, edges, unlinked } = useMemo(() => {
    const graph = buildGraph(structure);
    const laidOut = layoutGraph(graph.nodes, graph.edges);
    return {
      positionedNodes: laidOut.nodes,
      edges: laidOut.edges,
      unlinked: graph.unlinked,
    };
  }, [structure]);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const sync = () =>
      setCanvasSize({ width: el.clientWidth, height: el.clientHeight });
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const translateExtent = useMemo(
    () => computeTranslateExtent(positionedNodes, canvasSize),
    [positionedNodes, canvasSize],
  );

  const zoomRange = useMemo(
    () =>
      ReadableViewportHelper.getZoomRange({
        nodePositions: positionedNodes.map((n) => n.position),
        nodeSize: ENTITY_NODE_SIZE,
        canvasSize: canvasSize ?? { width: 0, height: 0 },
        padding: ReadableViewportHelper.DEFAULT_PADDING,
        minZoom: ReadableViewportHelper.ORG_MIN_ZOOM,
      }),
    [positionedNodes, canvasSize]
  );

  return (
    <div className="flex h-full w-full flex-col gap-xl">
      <div
        ref={canvasRef}
        className="org-diagram-canvas relative flex-1 overflow-hidden rounded-sm border-[0.5px] border-border-primary bg-bg-light shadow-[inset_0px_2px_8px_0px_rgba(0,0,0,0.04)]"
      >
        {/* xyflow defaults `.react-flow__edge` to cursor:pointer. Edges are
            non-interactive — make them feel like canvas (grab, then grabbing
            while the mouse is down) so the cursor doesn't flicker as the
            user pans across them. */}
        <style>{`
          .org-diagram-canvas .react-flow__edge,
          .org-diagram-canvas .react-flow__edge-path,
          .org-diagram-canvas .react-flow__edge-interaction {
            cursor: grab;
          }
          .org-diagram-canvas .react-flow__edge:active,
          .org-diagram-canvas .react-flow__edge-path:active,
          .org-diagram-canvas .react-flow__edge-interaction:active {
            cursor: grabbing;
          }
        `}</style>
        {/* Provider wraps chart + unlinked panel so the panel can read canvas
            zoom via useStore. Renders even when empty so all-unlinked case
            still gets the panel. */}
        <ReactFlowProvider>
          {positionedNodes.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <ChartViewportSync
                nodes={positionedNodes}
                nodeSize={ENTITY_NODE_SIZE}
                minZoom={ReadableViewportHelper.ORG_MIN_ZOOM}
              />
              <ReactFlow
                nodes={positionedNodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                edgeTypes={EDGE_TYPES}
                translateExtent={translateExtent}
                minZoom={zoomRange.minZoom}
                maxZoom={zoomRange.maxZoom}
                // Read-only: pan/zoom + node click only.
                nodesDraggable={false}
                nodesConnectable={false}
                edgesFocusable={false}
                elementsSelectable={Boolean(onEntityClick)}
                // Trackpad UX (Mac two-finger gestures, Windows precision
                // touchpads): wheel events pan instead of zoom — pinch and
                // Cmd/Ctrl+scroll keep their zoom semantics. Matches
                // Figma/Miro/Notion canvas conventions.
                panOnScroll
                // 1.0 ≈ "1 scroll-pixel = 1 pan-pixel"; the default 0.5
                // feels sluggish on Apple trackpads where momentum scroll
                // already dampens the gesture.
                panOnScrollSpeed={1}
                proOptions={{ hideAttribution: true }}
                onNodeClick={(_e, node) => {
                  // Externals (non-controlled entities) render disabled and
                  // must not navigate — they're shown for context only.
                  if (
                    (node.data as EntityNodeType['data'])?.isNonControlledEntity
                  ) {
                    return;
                  }
                  onEntityClick?.(Number(node.id));
                }}
                // Override xyflow's white canvas + #b1b1b7 edges so they track
                // the wrapper bg + border-primary token. OwnershipEdge also pins
                // stroke inline since selected/hover route through different vars.
                style={{
                  background: 'transparent',
                  ['--xy-edge-stroke' as string]: '#DEDBD4',
                }}
              />
              {!hideToolbar && (
                <ChartMinimap
                  nodes={positionedNodes}
                  nodeSize={ENTITY_NODE_SIZE}
                />
              )}
              {!hideToolbar && (
                <ToolbarWithExport
                  nodes={positionedNodes}
                  companyId={companyId}
                  companyName={companyName}
                  onExportPngOverride={onExportPng}
                  onExportPdfOverride={onExportPdf}
                  onCopyPngOverride={onCopyPng}
                />
              )}
              {onReady && <CaptureGate onReady={onReady} />}
            </>
          )}
          {!hideUnlinkedPanel && (
            <UnlinkedEntitiesPanel
              entities={unlinked}
              onEntityClick={onUnlinkedClick ?? onEntityClick}
            />
          )}
          {topLeftSlot && (
            // Inline top/left so spacing-token classes aren't dropped by the
            // consuming app's content scanner from this package.
            <div className="absolute z-10" style={{ top: 12, left: 12 }}>
              {topLeftSlot}
            </div>
          )}
          {topRightSlot && (
            <div className="absolute z-10" style={{ top: 12, right: 12 }}>
              {topRightSlot}
            </div>
          )}
        </ReactFlowProvider>
      </div>
    </div>
  );
};

export { OrgDiagram };
